import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import newLogo from '../assets/robomind-logo.svg';
import { SOLUTIONS } from '../data/solutions';

const Footer = () => {
    const { t } = useTranslation();

    const footerLink = "text-gray-600 hover:text-primary transition duration-150 ease-in-out";

    return (
        <footer>
            <div className="max-w-full mx-auto px-4 sm:px-6 bg-gray-100 border-t">
                {/* Top area: Blocks */}
                <div className="grid sm:grid-cols-12 gap-8 py-8 md:py-12 border-t border-gray-200">

                    {/* 1st block: Company Info */}
                    <div className="col-span-12 lg:col-span-4 lg:ml-4">
                        <div className="mb-4">
                            <Link to="/" aria-label="ROBOMIND Systems - Home">
                                <img src={newLogo} alt="ROBOMIND Systems logo" className="h-12" loading="lazy" decoding="async" />
                            </Link>
                        </div>
                        <div className='text-sm text-gray-600 space-y-2'>
                            <div>
                                <p className="font-bold">{t('footer.company_info.address_label')}:</p>
                                <p>{t('footer.company_info.address_value')}</p>
                            </div>
                            <div>
                                <p className="font-bold">{t('footer.company_info.szekhely_label')}:</p>
                                <p>{t('footer.company_info.szekhely_value')}</p>
                            </div>
                            <div className="pt-2">
                                <p><span className="font-semibold">{t('footer.company_info.reg_number')}:</span> {t('imprint_page.reg_number_value')}</p>
                                <p><span className="font-semibold">{t('footer.company_info.tax_number')}:</span> {t('footer.company_info.tax_number_value')}</p>
                            </div>
                        </div>
                    </div>

                    {/* 2nd block: Quick Links */}
                    <div className="col-span-6 md:col-span-3 lg:col-span-2">
                        <p className="text-gray-800 font-bold mb-2">{t('footer.links.title')}</p>
                        <ul className="text-md">
                            <li className="mb-2"><Link to="/about" className={footerLink}>{t('nav.about')}</Link></li>
                            <li className="mb-2"><Link to="/technology" className={footerLink}>{t('nav.technology')}</Link></li>
                            <li className="mb-2"><Link to="/industries" className={footerLink}>{t('nav.industries')}</Link></li>
                            <li className="mb-2"><Link to="/case-studies" className={footerLink}>{t('nav.case_studies')}</Link></li>
                            <li className="mb-2"><Link to="/blog" className={footerLink}>{t('nav.blog')}</Link></li>
                            <li className="mb-2"><Link to="/contact" className={footerLink}>{t('nav.contact')}</Link></li>
                            <li className="mb-2"><Link to="/imprint" className={footerLink}>{t('footer.links.imprint')}</Link></li>
                            <li className="mb-2"><Link to="/gtc" className={footerLink}>{t('footer.links.gtc')}</Link></li>
                            <li className="mb-2"><Link to="/privacy-policy" className={footerLink}>{t('footer.links.privacy_policy')}</Link></li>
                        </ul>
                    </div>

                    {/* 3rd block: Solutions */}
                    <div className="col-span-6 md:col-span-3 lg:col-span-2">
                        <p className="text-gray-800 font-bold mb-2">{t('footer.solutions.title')}</p>
                        <ul className="text-md">
                            {SOLUTIONS.map((s) => (
                                <li key={s.slug} className="mb-2">
                                    <Link to={`/solutions/${s.slug}`} className={footerLink}>
                                        {t(`solutions.${s.key}.title`)}
                                    </Link>
                                </li>
                            ))}
                            <li className="mb-2">
                                <Link to="/solutions" className={`${footerLink} font-bold`}>
                                    {t('common.all_solutions')} →
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* 4th block: Social Links */}
                    <div className="col-span-12 md:col-span-6 lg:col-span-4">
                        <p className="text-gray-800 font-bold mb-2">{t('footer.social.title')}</p>
                        <ul className="flex mb-4 md:mb-0">
                            {/* -- LinkedIn -- */}
                            <li>
                                <a href="https://www.linkedin.com/company/robomindsystems" target="_blank" rel="noreferrer" className="flex justify-center items-center text-primary hover:text-accent bg-white rounded-full shadow transition duration-150 ease-in-out" aria-label="LinkedIn">
                                    <svg className="w-8 h-8 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="4.983" cy="5.009" r="2.188"></circle><path d="M9.237 8.855v12.139h3.769v-6.003c0-1.584.298-3.118 2.262-3.118 1.937 0 1.961 1.811 1.961 3.218v5.904H21v-6.657c0-3.27-.704-5.783-4.526-5.783-1.835 0-3.065 1.007-3.568 1.96h-.051v-1.66H9.237zm-6.142 0H6.87v12.139H3.095z"></path>
                                    </svg>
                                </a>
                            </li>
                            {/* -- Facebook -- */}
                            <li className="ml-4">
                                <a href="https://facebook.com/robomindsystems" target="_blank" rel="noreferrer" className="flex justify-center items-center text-primary hover:text-accent bg-white rounded-full shadow transition duration-150 ease-in-out" aria-label="Facebook">
                                    <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14.023 24L14 17h-3v-3h3v-2c0-2.7 1.672-4 4.08-4 1.153 0 2.144.086 2.433.124v2.821h-1.67c-1.31 0-1.563.623-1.563 1.536V14H21l-1 3h-2.72v7h-3.257z" />
                                    </svg>
                                </a>
                            </li>
                             {/* -- YouTube -- */}
                             <li className="ml-4">
                                <a href="https://youtube.com/@robomindsystems" target="_blank" rel="noreferrer" className="flex justify-center items-center text-primary hover:text-accent bg-white rounded-full shadow transition duration-150 ease-in-out" aria-label="YouTube">
                                    <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 16 3.545 16 3.545s-3.505 0-5.377.505A3.017 3.017 0 0 0 8.502 6.186C8 8.056 8 12 8 12s0 3.944.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 5.377.505 5.377.505s3.505 0 5.377-.505a3.016 3.016 0 0 0 2.122-2.136C24 15.944 24 12 24 12s0-3.944-.502-5.814zM14.545 15.568V8.432L20 12l-5.455 3.568z"/>
                                    </svg>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Bottom area */}
                <div className="py-4 md:py-8 border-t border-gray-200">
                    <div className="text-sm text-gray-700 text-center">
                        &copy; {new Date().getFullYear()} ROBOMIND Kft. {t('footer.rights_reserved')}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
